import React, { useState, useRef, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  IconButton, 
  Button, 
  Menu, 
  MenuItem, 
  TextField, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Tooltip,
  Snackbar,
  Alert,
  InputAdornment
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  Bold, 
  Italic, 
  Underline, 
  AlignLeft, 
  AlignCenter, 
  AlignRight, 
  Image, 
  Save,
  HelpCircle,
  FileText,
  Download,
  Send,
  Undo,
  Edit as EditIcon,
  Plus as AddIcon,
  DoorClosed as CloseIcon
} from 'lucide-react';
import PaymentPanel from './PaymentPanel.tsx';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import GradientText from '../common/GradientText.tsx';
import ProblemSourcingDrawer from '../workspace/ProblemSourcingDrawer.tsx';

const EditorContainer = styled(Box)(({ theme }) => ({
  height: 'calc(100vh - 200px)',
  display: 'flex',
  flexDirection: 'column',
  background: 'rgba(26, 26, 46, 0.6)',
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  border: '1px solid rgba(138, 43, 226, 0.2)',
}));

const Toolbar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1, 2),
  background: 'rgba(26, 26, 46, 0.8)',
  backdropFilter: 'blur(10px)',
  borderBottom: '1px solid rgba(138, 43, 226, 0.2)',
  flexWrap: 'wrap',
}));

const ToolbarGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginRight: theme.spacing(2),
  '&:not(:last-child)': {
    borderRight: '1px solid rgba(138, 43, 226, 0.2)',
    paddingRight: theme.spacing(2),
  }
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.7)',
  padding: theme.spacing(0.5),
  margin: theme.spacing(0, 0.5),
  '&:hover': {
    background: 'rgba(138, 43, 226, 0.1)',
    color: '#fff',
  },
}));

const AIAssistButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, #8A2BE2 30%, #9370DB 90%)',
  color: '#fff',
  padding: theme.spacing(0.5, 2),
  borderRadius: theme.spacing(4),
  fontSize: '0.8rem',
  marginLeft: 'auto',
  '&:hover': {
    background: 'linear-gradient(45deg, #9370DB 30%, #8A2BE2 90%)',
    boxShadow: '0 0 10px rgba(138, 43, 226, 0.5)',
  },
}));

const FooterToolbar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1, 2),
  background: 'rgba(26, 26, 46, 0.8)',
  backdropFilter: 'blur(10px)',
  borderTop: '1px solid rgba(138, 43, 226, 0.2)',
}));

interface DocumentEditorProps {
  initialContent?: string;
  onSave?: (content: string) => void;
  onAddToProposal?: (content: string, cost: number) => void;
  problemText?: string;
  setProblemText?: (text: string) => void;
}

const DocumentEditor: React.FC<DocumentEditorProps> = ({ 
  initialContent = '', 
  onSave,
  onAddToProposal,
  problemText,
  setProblemText
}) => {
  const [content, setContent] = useState(initialContent);
  const [fontSizeAnchorEl, setFontSizeAnchorEl] = useState<null | HTMLElement>(null);
  const [fontSize, setFontSize] = useState('1rem');
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [aiCost, setAiCost] = useState(2500);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [showProblemSourcingDrawer, setShowProblemSourcingDrawer] = useState(false);
  const [localProblemText, setLocalProblemText] = useState(problemText || '');
  const [isEditingProblem, setIsEditingProblem] = useState(false);
  const [problemStatement, setProblemStatement] = useState(localProblemText || 'Define your problem here...');
  const [editedProblemStatement, setEditedProblemStatement] = useState(problemStatement);
  
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLocalProblemText(problemText || '');
  }, [problemText]);

  useEffect(() => {
    if (localProblemText) {
      setProblemStatement(localProblemText);
    }
  }, [localProblemText]);

  const handleFormatCommand = (command: string, value: string = '') => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      editorRef.current.focus();
    }
  };

  const handleFontSizeClick = (event: React.MouseEvent<HTMLElement>) => {
    setFontSizeAnchorEl(event.currentTarget);
  };

  const handleFontSizeClose = () => {
    setFontSizeAnchorEl(null);
  };

  const handleFontSizeSelect = (size: string) => {
    setFontSize(size);
    handleFormatCommand('fontSize', size === '1rem' ? '3' : size === '1.25rem' ? '4' : '5');
    handleFontSizeClose();
  };

  const handleAIAssistClick = () => {
    setAiDialogOpen(true);
  };

  const handleAIDialogClose = () => {
    setAiDialogOpen(false);
  };

  const handleAIRequest = () => {
    // Here you would call your API to get AI-generated content
    // For now, let's simulate it
    setAiDialogOpen(false);
    setPaymentDialogOpen(true);
  };

  const handlePaymentComplete = () => {
    setPaymentDialogOpen(false);
    
    // Simulate AI response after payment
    setTimeout(() => {
      const aiGeneratedContent = `<p><strong>AI-generated content based on your prompt:</strong></p>
        <p>${aiPrompt}</p>
        <p>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nullam auctor, nisl eget ultricies tincidunt, 
        nisl nisl aliquam nisl, eget ultricies nisl nisl eget nisl. Nullam auctor, nisl eget ultricies tincidunt.</p>`;
      
      if (editorRef.current) {
        const currentContent = editorRef.current.value;
        setContent(currentContent + aiGeneratedContent);
      }
      
      setSnackbarMessage('AI content added successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    }, 1000);
  };

  const handleSaveDraft = () => {
    if (editorRef.current) {
      const contentToSave = editorRef.current.value;
      setContent(contentToSave);
      
      // Call the onSave prop if provided
      if (onSave) {
        onSave(contentToSave);
      }
      
      setSnackbarMessage('Draft saved successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    }
  };

  const handleAddToProposal = () => {
    if (!editorRef.current) return;
    
    // Extract content from the editor
    const documentContent = editorRef.current.value;
    
    // Calculate the cost based on content length
    const contentLength = documentContent.length;
    const baseCost = 1000;
    const costPerChar = 0.5;
    const totalCost = Math.round(baseCost + (contentLength * costPerChar));
    
    // Create comprehensive proposal data including problem statement and editor content
    const proposalData = {
      id: `proposal-${Date.now()}`,
      title: problemStatement.substring(0, 50) + (problemStatement.length > 50 ? '...' : ''),
      description: problemStatement,
      content: documentContent,
      createdAt: new Date().toISOString(),
      estimatedTimeline: '3 months',
      timelineType: 'initiative',
      status: 'draft',
      cost: totalCost,
      problemStatement: problemStatement,  // Add the problem statement separately
      documentType: 'document',            // Indicate this is from document editor
      uploadedFiles: [],                   // Placeholder for any uploaded files
      linkedSources: []                    // Placeholder for any linked sources
    };
    
    // Call the onAddToProposal prop with the enhanced data
    if (onAddToProposal) {
      // For backward compatibility, still pass the two expected arguments
      onAddToProposal(documentContent, totalCost);
      
      // You could also store the full proposal data in localStorage for persistence
      const existingProposals = JSON.parse(localStorage.getItem('proposals') || '[]');
      localStorage.setItem('proposals', JSON.stringify([...existingProposals, proposalData]));
      
      console.log('Creating proposal with full data:', proposalData);
      
      // Display success message
      setSnackbarMessage(`Added to proposal (Cost: $${totalCost.toLocaleString()})`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    }
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadDialogOpen(true);
      // In a real application, you would handle the file upload here
    }
  };

  const handleExport = () => {
    if (editorRef.current) {
      const content = editorRef.current.value;
      const blob = new Blob([content], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'document.html';
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newText = e.target.value;
    setLocalProblemText(newText);
    if (setProblemText) {
      setProblemText(newText);
    }
  };
  

  const safeSetShowProblemSourcingDrawer = (show: boolean) => {
    setShowProblemSourcingDrawer(show);
  };

  const handleEditProblem = () => {
    setEditedProblemStatement(problemStatement);
    setIsEditingProblem(true);
  };

  const handleCancelEditProblem = () => {
    setIsEditingProblem(false);
  };

  const handleSaveProblem = () => {
    setProblemStatement(editedProblemStatement);
    setIsEditingProblem(false);
    // Also update the main problem text if setProblemText function is provided
    if (setProblemText) {
      setProblemText(editedProblemStatement);
    }
    // Update local problem text state
    setLocalProblemText(editedProblemStatement);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Problem Input Section */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          backgroundColor: 'rgba(26, 26, 46, 0.7)',
          borderRadius: 1,
          border: '1px solid rgba(138, 43, 226, 0.3)',
        }}
      >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <GradientText gradient="secondary" variant="h5" sx={{ fontWeight: 600 }}>
            Forge a Problem Statement
          </GradientText>
          <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" sx={{ mt: 1 }}>
            Be specific and include relevant details to get the best solutions
          </Typography>
        </Box>
      </Box>
      <Paper 
                sx={{ 
                  p: 2, 
                  mt: 2, 
                  background: 'rgba(26, 26, 46, 0.7)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(138, 43, 226, 0.2)',
                }}
              >
                {isEditingProblem ? (
                  <Box>
                    <textarea
                      value={editedProblemStatement}
                      onChange={(e) => setEditedProblemStatement(e.target.value)}
                      style={{
                        width: '100%',
                        minHeight: '70px',
                        padding: '8px',
                        background: 'rgba(26, 26, 46, 0.5)',
                        color: '#fff',
                        border: '1px solid rgba(138, 43, 226, 0.3)',
                        borderRadius: '4px',
                        resize: 'vertical',
                        fontFamily: 'inherit',
                        fontSize: 'inherit',
                      }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
                      <Button 
                        variant="outlined" 
                        size="small" 
                        onClick={handleCancelEditProblem}
                        sx={{ 
                          borderColor: 'rgba(138, 43, 226, 0.5)',
                          color: '#fff',
                          '&:hover': {
                            borderColor: 'rgba(138, 43, 226, 0.8)',
                            background: 'rgba(138, 43, 226, 0.1)',
                          }
                        }}
                      >
                        Cancel
                      </Button>
                      <Button 
                        variant="contained" 
                        size="small" 
                        onClick={handleSaveProblem}
                        sx={{ 
                          background: 'linear-gradient(45deg, #8A2BE2 30%, #9370DB 90%)',
                          color: '#fff',
                          '&:hover': {
                            background: 'linear-gradient(45deg, #9370DB 30%, #8A2BE2 90%)',
                          }
                        }}
                      >
                        Save
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="body1">
                      {problemStatement}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                      <Button 
                        variant="text" 
                        size="small" 
                        onClick={handleEditProblem}
                        startIcon={<EditIcon />}
                        sx={{ 
                          color: '#00CC99',
                          '&:hover': {
                            background: 'rgba(0, 204, 153, 0.1)',
                          }
                        }}
                      >
                        Edit
                      </Button>
                    </Box>
                  </Box>
                )}
              </Paper>

      <EditorContainer>
        <Toolbar>
          <ToolbarGroup>
            <Tooltip title="Bold">
              <StyledIconButton onClick={() => handleFormatCommand('bold')}>
                <Bold size={18} />
              </StyledIconButton>
            </Tooltip>
            <Tooltip title="Italic">
              <StyledIconButton onClick={() => handleFormatCommand('italic')}>
                <Italic size={18} />
              </StyledIconButton>
            </Tooltip>
            <Tooltip title="Underline">
              <StyledIconButton onClick={() => handleFormatCommand('underline')}>
                <Underline size={18} />
              </StyledIconButton>
            </Tooltip>
          </ToolbarGroup>
          
          <ToolbarGroup>
            <Tooltip title="Font Size">
              <StyledIconButton onClick={handleFontSizeClick}>
                <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                  {fontSize}
                </Typography>
              </StyledIconButton>
            </Tooltip>
            <Menu
              anchorEl={fontSizeAnchorEl}
              open={Boolean(fontSizeAnchorEl)}
              onClose={handleFontSizeClose}
            >
              <MenuItem onClick={() => handleFontSizeSelect('1rem')}>Small</MenuItem>
              <MenuItem onClick={() => handleFontSizeSelect('1.25rem')}>Medium</MenuItem>
              <MenuItem onClick={() => handleFontSizeSelect('1.5rem')}>Large</MenuItem>
            </Menu>
          </ToolbarGroup>
          
          <ToolbarGroup>
            <Tooltip title="Align Left">
              <StyledIconButton onClick={() => handleFormatCommand('justifyLeft')}>
                <AlignLeft size={18} />
              </StyledIconButton>
            </Tooltip>
            <Tooltip title="Align Center">
              <StyledIconButton onClick={() => handleFormatCommand('justifyCenter')}>
                <AlignCenter size={18} />
              </StyledIconButton>
            </Tooltip>
            <Tooltip title="Align Right">
              <StyledIconButton onClick={() => handleFormatCommand('justifyRight')}>
                <AlignRight size={18} />
              </StyledIconButton>
            </Tooltip>
          </ToolbarGroup>
          
          <ToolbarGroup>
            <Tooltip title="Insert Image">
              <StyledIconButton onClick={handleUploadClick}>
                <Image size={18} />
              </StyledIconButton>
            </Tooltip>
            <input 
              type="file" 
              ref={fileInputRef} 
              style={{ display: 'none' }} 
              accept="image/*" 
              onChange={handleFileSelected} 
            />
          </ToolbarGroup>
          
          <ToolbarGroup>
            <Tooltip title="Undo">
              <StyledIconButton onClick={() => {
                document.execCommand('undo', false);
                // Make sure we get the updated content after undo
                if (editorRef.current) {
                  setContent(editorRef.current.value);
                }
              }}>
                <Undo size={18} />
              </StyledIconButton>
            </Tooltip>
          </ToolbarGroup>
          
          <AIAssistButton 
            startIcon={<HelpCircle size={16} />}
            onClick={handleAIAssistClick}
          >
            AI Assist
          </AIAssistButton>
        </Toolbar>
        
        <Box sx={{ 
          flex: 1, 
          '& .quill': {
            height: 'calc(100% - 20px)',
            display: 'flex',
            flexDirection: 'column'
          },
          '& .ql-container': {
            flex: 1,
            overflow: 'auto',
            fontSize: '1rem',
            fontFamily: 'inherit',
            color: '#fff',
            background: 'rgba(26, 26, 46, 0.4)',
          },
          '& .ql-editor': {
            height: '100%',
            direction: 'ltr',
            textAlign: 'left',
          },
          '& .ql-toolbar': {
            background: 'rgba(26, 26, 46, 0.8)',
            borderColor: 'rgba(138, 43, 226, 0.2)',
          },
          '& .ql-snow .ql-stroke': {
            stroke: '#fff',
          },
          '& .ql-snow .ql-fill': {
            fill: '#fff',
          },
          '& .ql-snow .ql-picker': {
            color: '#fff',
          }
        }}>
          {/* @ts-ignore */}
          <ReactQuill
            theme="snow"
            value={content}
            onChange={setContent}
            modules={{
              toolbar: [
                ['bold', 'italic', 'underline', 'strike'],
                [{ 'align': [] }],
                //[{ 'list': 'ordered'}, { 'list': 'bullet' }],
                ['undo', 'redo'],
              ]
            }}
          />
        </Box>
        
        <FooterToolbar>
          <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
            <Box>
              <Button
                variant="outlined"
                startIcon={<Save size={16} />}
                onClick={handleSaveDraft}
                sx={{ 
                  borderColor: 'rgba(138, 43, 226, 0.5)',
                  color: '#fff',
                  mr: 1,
                  '&:hover': {
                    borderColor: 'rgba(138, 43, 226, 0.8)',
                    background: 'rgba(138, 43, 226, 0.1)',
                  }
                }}
              >
                Save Draft
              </Button>
              <Button
                variant="outlined"
                startIcon={<Download size={16} />}
                onClick={handleExport}
                sx={{ 
                  borderColor: 'rgba(0, 204, 153, 0.5)',
                  color: '#00CC99',
                  '&:hover': {
                    borderColor: 'rgba(0, 204, 153, 0.8)',
                    background: 'rgba(0, 204, 153, 0.1)',
                  }
                }}
              >
                Export
              </Button>
            </Box>
            
            <Button
              variant="contained"
              endIcon={<Send size={16} />}
              onClick={handleAddToProposal}
              sx={{
                background: 'linear-gradient(45deg, #00CC99 30%, #8A2BE2 90%)',
                color: '#fff',
                '&:hover': {
                  background: 'linear-gradient(45deg, #00CC99 40%, #8A2BE2 100%)',
                  boxShadow: '0 0 10px rgba(0, 204, 153, 0.5)',
                }
              }}
            >
              Add to Proposal
            </Button>
          </Box>
        </FooterToolbar>
      </EditorContainer>
      
      {/* AI Assist Dialog */}
      <Dialog open={aiDialogOpen} onClose={handleAIDialogClose} maxWidth="md">
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <HelpCircle size={20} style={{ marginRight: 8 }} />
            AI Writing Assistant
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            What would you like the AI to help you with? Describe your request below:
          </Typography>
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            placeholder="E.g., Write a compelling introduction for my AI-based customer service solution proposal..."
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
          />
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            mt: 3, 
            p: 2, 
            bgcolor: 'rgba(0, 204, 153, 0.1)', 
            borderRadius: 1 
          }}>
            <Typography variant="body2">
              AI Assistance Cost:
            </Typography>
            <Typography variant="h6" sx={{ color: '#00CC99', fontWeight: 600 }}>
              ${aiCost.toLocaleString()}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAIDialogClose} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleAIRequest} 
            variant="contained"
            disabled={!aiPrompt.trim()}
            sx={{
              background: 'linear-gradient(45deg, #00CC99 30%, #8A2BE2 90%)',
              color: '#fff',
            }}
          >
            Process Request
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FileText size={20} style={{ marginRight: 8 }} />
            Complete Payment for AI Assistance
          </Box>
        </DialogTitle>
        <DialogContent>
          <PaymentPanel 
            proposalId={null} 
            solutions={[]} 
            onPaymentComplete={handlePaymentComplete}
          />
        </DialogContent>
      </Dialog>
      
      {/* File Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)}>
        <DialogTitle>Upload File</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            File upload functionality will be implemented here.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button 
            variant="contained"
            sx={{
              background: 'linear-gradient(45deg, #00CC99 30%, #8A2BE2 90%)',
              color: '#fff',
            }}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          sx={{ 
            width: '100%',
            background: snackbarSeverity === 'success' ? 'rgba(0, 204, 153, 0.2)' : 'rgba(244, 67, 54, 0.2)',
            color: snackbarSeverity === 'success' ? '#00CC99' : '#f44336',
            border: `1px solid ${snackbarSeverity === 'success' ? 'rgba(0, 204, 153, 0.5)' : 'rgba(244, 67, 54, 0.5)'}`,
            backdropFilter: 'blur(10px)',
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'rgba(255, 255, 255, 0.7)' }}>
        Try out advanced problem sourcing with voice inputs, uploading documents, links and more.
      </Typography>
      <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Start working on a new Problem"
              variant="outlined"
              value={localProblemText}
              onChange={handleTextChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Submit problem to LLM">
                      <IconButton 
                        edge="end" 
                        onClick={() => {
                          if (localProblemText) {
                            console.log('Sending to LLM:', localProblemText);
                          }
                        }}
                        disabled={!localProblemText}
                        sx={{
                          color: '#00CC99',
                          '&.Mui-disabled': {
                            color: 'rgba(0, 204, 153, 0.3)',
                          }
                        }}
                      >
                        <Send />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
                startAdornment: (
                  <InputAdornment position="start">
                    <Tooltip title="Open problem submission drawer">
                      <IconButton 
                        edge="start" 
                        onClick={() => safeSetShowProblemSourcingDrawer(true)}
                        sx={{ color: 'rgba(138, 43, 226, 0.7)' }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
                sx: {
                  backgroundColor: 'rgba(26, 26, 46, 0.4)',
                  color: '#fff',
                  '&.Mui-focused': {
                    boxShadow: '0 0 0 2px rgba(138, 43, 226, 0.5)',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(138, 43, 226, 0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(138, 43, 226, 0.5)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(138, 43, 226, 0.7)',
                  },
                }
              }}
              sx={{
                '& .MuiInputBase-input': {
                  color: '#fff',
                },
              }}
            />
            
            <Button
              variant="outlined"
              startIcon={showProblemSourcingDrawer ? <CloseIcon /> : <AddIcon />}
              onClick={() => safeSetShowProblemSourcingDrawer(!showProblemSourcingDrawer)}
              sx={{
                mt: 1,
                borderColor: 'rgba(138, 43, 226, 0.5)',
                color: 'rgba(255, 255, 255, 0.9)',
                '&:hover': {
                  borderColor: '#8A2BE2',
                  backgroundColor: 'rgba(138, 43, 226, 0.1)',
                }
              }}
            >
              <GradientText gradient="secondary" variant="body2">
                {showProblemSourcingDrawer ? 'Close Problem Sourcing' : 'Use Advanced Problem Sourcing'}
              </GradientText>
            </Button>
      </Paper>

      {showProblemSourcingDrawer && (
        <ProblemSourcingDrawer 
          onSubmit={(problem) => {
            // Update local state
            setLocalProblemText(problem);
            setProblemStatement(problem);
            
            // Update parent state if available
            if (setProblemText) {
              setProblemText(problem);
            }
            
            // Close the drawer
            safeSetShowProblemSourcingDrawer(false);
            
            // Show confirmation
            setSnackbarMessage('Problem statement updated');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
          }}
          onClose={() => safeSetShowProblemSourcingDrawer(false)}
        />
      )}
    </Box>
  );
};

export default DocumentEditor; 